import { CameraPhoto, DeviceAccessor, HorizontalAlignment, LogManager, VerticalAlignment } from "@mcleod/core";
import { BehaviorTypes, ClickEvent, Image, ImageViewer, ImageViewerValues, Label, Panel } from "@mcleod/components";
import { ImageName } from "@mcleod/images";
import { CapturedImage, createCapturedImage } from "./CapturedImage";
import { CapturedImageThumbnail } from "./CapturedImageThumbnail";
import { ImageBundleBehavior } from "./ImageBundleBehavior";
import { ImageBundleType, getImageBundleTypeLabel } from "./ImageBundleType";
import { ImageBundleStyles } from "./ImageBundleStyles";

const log = LogManager.getLogger("capture.ImageBundle");

/**
 * Props for ImageBundle component.
 */
export interface ImageBundleProps {
    /** The type of this image bundle */
    bundleType: ImageBundleType;
    /** Callback when images change (add/remove) */
    onImagesChanged?: (images: CapturedImage[], bundleType: ImageBundleType) => void;
    /** Custom title override (otherwise uses bundle type label) */
    title?: string;
}

/**
 * Represents a bundle of captured images with an "Add Page" button.
 * Supports horizontal scrolling of thumbnails and camera capture.
 */
export class ImageBundle extends Panel {
    private static _behavior: ImageBundleBehavior;
    private _bundleType: ImageBundleType;
    private _images: CapturedImage[] = [];
    private _onImagesChanged: (images: CapturedImage[], bundleType: ImageBundleType) => void;

    private _headerPanel: Panel;
    private _titleLabel: Label;
    private _countLabel: Label;
    private _thumbnailScroller: Panel;
    private _addPageButton: Panel;

    constructor(props?: Partial<ImageBundleProps>) {
        super({}, false);

        this._bundleType = props?.bundleType ?? ImageBundleType.FREIGHT_PHOTOS;
        this._onImagesChanged = props?.onImagesChanged;

        this.setProps({
            fillRow: true,
            padding: 0,
            margin: 0,
            marginBottom: 12,
            borderRadius: 12,
            backgroundColor: "defaultBackground",
            borderShadow: true
        });

        this._element.classList.add(ImageBundleStyles.bundleContainer);

        this.createHeader(props?.title);
        this.createThumbnailScroller();
        this.createAddPageButton();

        this.add(this._headerPanel, this._thumbnailScroller);

        // Add the "Add Page" button to the scroller
        this._thumbnailScroller.add(this._addPageButton);

        this.updateCountLabel();
    }

    /**
     * Creates the header panel with title and count.
     */
    private createHeader(customTitle?: string): void {
        this._headerPanel = new Panel({
            fillRow: true,
            padding: 12,
            paddingLeft: 16,
            paddingRight: 16,
            rowBreak: true
        });
        this._headerPanel._element.classList.add(ImageBundleStyles.bundleHeader);

        const titleText = customTitle ?? getImageBundleTypeLabel(this._bundleType);
        this._titleLabel = new Label({
            text: titleText,
            fontBold: true,
            fontSize: "large",
            rowBreak: false
        });
        this._titleLabel._element.classList.add(ImageBundleStyles.bundleTitle);

        this._countLabel = new Label({
            text: "(0)",
            fontSize: "medium",
            color: "subtle.dark",
            rowBreak: false,
            marginLeft: 8
        });
        this._countLabel._element.classList.add(ImageBundleStyles.bundleCount);

        this._headerPanel.add(this._titleLabel, this._countLabel);
    }

    /**
     * Creates the horizontal scrolling thumbnail container.
     */
    private createThumbnailScroller(): void {
        this._thumbnailScroller = new Panel({
            fillRow: true,
            padding: 12,
            rowBreak: true,
            wrap: false,
            scrollX: true,
            scrollY: false
        });
        this._thumbnailScroller._element.classList.add(ImageBundleStyles.thumbnailScroller);
        // Hide scrollbar but allow scrolling
        this._thumbnailScroller._element.style.scrollbarWidth = "none";
        (this._thumbnailScroller._element.style as any).msOverflowStyle = "none";
    }

    /**
     * Creates the "Add Page" button.
     */
    private createAddPageButton(): void {
        const behaviors = this.behavior;

        this._addPageButton = new Panel({
            width: behaviors.addPageButtonWidth,
            height: behaviors.addPageButtonHeight,
            minWidth: behaviors.addPageButtonWidth,
            minHeight: behaviors.addPageButtonHeight,
            padding: 8,
            rowBreak: false,
            verticalAlign: VerticalAlignment.CENTER,
            align: HorizontalAlignment.CENTER
        });
        this._addPageButton._element.classList.add(ImageBundleStyles.addPageButton);

        const addIcon = new Image({
            name: ImageName.add,
            width: behaviors.addPageIconSize,
            height: behaviors.addPageIconSize,
            color: "primary",
            rowBreak: true,
            align: HorizontalAlignment.CENTER
        });
        addIcon._element.classList.add(ImageBundleStyles.addPageIcon);

        const addLabel = new Label({
            text: "Add Page",
            fontSize: behaviors.addPageLabelFontSize,
            color: "primary",
            fontBold: true,
            rowBreak: true,
            align: HorizontalAlignment.CENTER
        });
        addLabel._element.classList.add(ImageBundleStyles.addPageText);

        this._addPageButton.add(addIcon, addLabel);
        this._addPageButton.addClickListener((event: ClickEvent) => this.handleAddPageTap(event));
    }

    /**
     * Gets the bundle type.
     */
    get bundleType(): ImageBundleType {
        return this._bundleType;
    }

    /**
     * Gets the current captured images.
     */
    get images(): CapturedImage[] {
        return [...this._images];
    }

    /**
     * Gets the count of captured images.
     */
    get imageCount(): number {
        return this._images.length;
    }

    /**
     * Sets the images changed callback.
     */
    set onImagesChanged(callback: (images: CapturedImage[], bundleType: ImageBundleType) => void) {
        this._onImagesChanged = callback;
    }

    /**
     * Updates the count label to reflect current image count.
     */
    private updateCountLabel(): void {
        this._countLabel.text = `(${this._images.length})`;
    }

    /**
     * Handles tap on the "Add Page" button to capture a new image.
     */
    private async handleAddPageTap(event: ClickEvent): Promise<void> {
        event.shouldAutomaticallyStopPropagation = true;

        try {
            const cameraAccessor = DeviceAccessor.cameraAccessor;
            if (!cameraAccessor?.hasCamera) {
                log.info("Camera is not available on this device");
                return;
            }

            const photo = await cameraAccessor.takePhoto();

            if (photo) {
                this.addCapturedImage(photo);
            }
        } catch (error) {
            log.error("Failed to capture image:", error);
        }
    }

    /**
     * Adds a captured image to the bundle.
     */
    public addCapturedImage(photo: CameraPhoto): CapturedImage {
        const capturedImage = createCapturedImage(photo, this._bundleType);
        this._images.push(capturedImage);

        const thumbnail = this.createThumbnail(capturedImage);

        // Insert thumbnail before the "Add Page" button
        const addPageIndex = this._thumbnailScroller.components.indexOf(this._addPageButton);
        if (addPageIndex >= 0) {
            this._thumbnailScroller.insert(thumbnail, addPageIndex);
        } else {
            this._thumbnailScroller.add(thumbnail);
        }

        this.updateCountLabel();
        this.notifyImagesChanged();

        // Scroll to show the new thumbnail
        this.scrollToEnd();

        return capturedImage;
    }

    /**
     * Removes a captured image from the bundle.
     */
    public removeCapturedImage(imageId: string): boolean {
        const index = this._images.findIndex(img => img.id === imageId);
        if (index === -1) {
            return false;
        }

        this._images.splice(index, 1);

        // Find and remove the thumbnail component
        const thumbnail = this._thumbnailScroller.components.find(
            comp => comp instanceof CapturedImageThumbnail && comp.capturedImage?.id === imageId
        );
        if (thumbnail) {
            this._thumbnailScroller.remove(thumbnail);
        }

        this.updateCountLabel();
        this.notifyImagesChanged();

        return true;
    }

    /**
     * Clears all captured images from the bundle.
     */
    public clearImages(): void {
        this._images = [];

        // Remove all thumbnails but keep the "Add Page" button
        const componentsToRemove = this._thumbnailScroller.components.filter(
            comp => comp instanceof CapturedImageThumbnail
        );
        componentsToRemove.forEach(comp => this._thumbnailScroller.remove(comp));

        this.updateCountLabel();
        this.notifyImagesChanged();
    }

    /**
     * Creates a thumbnail component for a captured image.
     */
    private createThumbnail(capturedImage: CapturedImage): CapturedImageThumbnail {
        return new CapturedImageThumbnail({
            capturedImage,
            size: this.behavior.thumbnailSize,
            onTap: (img) => this.handleThumbnailTap(img),
            onDelete: (img) => this.handleThumbnailDelete(img)
        });
    }

    /**
     * Handles tap on a thumbnail to open full-screen view.
     */
    private async handleThumbnailTap(capturedImage: CapturedImage): Promise<void> {
        const photo = capturedImage.photo;

        // If we already have base64, use it directly
        if (photo.base64String) {
            this.showImageInViewer(capturedImage, photo.base64String);
            return;
        }

        // If we have a webPath, fetch the image and convert to base64
        if (photo.webPath) {
            try {
                const base64 = await this.fetchImageAsBase64(photo.webPath);
                if (base64) {
                    this.showImageInViewer(capturedImage, base64);
                    return;
                }
            } catch (error) {
                log.error("Failed to fetch image from webPath:", error);
            }
        }

        // If we have a dataUrl, extract the base64
        if (photo.dataUrl) {
            const base64 = this.extractBase64FromDataUrl(photo.dataUrl);
            if (base64) {
                this.showImageInViewer(capturedImage, base64);
                return;
            }
        }

        log.info("No valid image source for full-screen view");
    }

    /**
     * Shows an image in the ImageViewer.
     */
    private showImageInViewer(capturedImage: CapturedImage, base64: string): void {
        const imageValues = new ImageViewerValues(
            capturedImage.id,
            base64,
            `capture_${capturedImage.id}.${capturedImage.photo.format || "jpg"}`,
            ""
        );
        ImageViewer.viewImage(imageValues, {}, true);
    }

    /**
     * Fetches an image from a URL and returns it as base64.
     */
    private async fetchImageAsBase64(url: string): Promise<string | null> {
        try {
            const response = await fetch(url);
            if (!response.ok) {
                log.error(`Failed to fetch image: ${response.status} ${response.statusText}`);
                return null;
            }

            const blob = await response.blob();
            return new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    const dataUrl = reader.result as string;
                    // Extract base64 from data URL (remove "data:image/jpeg;base64," prefix)
                    const base64 = this.extractBase64FromDataUrl(dataUrl);
                    resolve(base64);
                };
                reader.onerror = () => reject(reader.error);
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            log.error("Error fetching image as base64:", error);
            return null;
        }
    }

    /**
     * Extracts base64 data from a data URL.
     */
    private extractBase64FromDataUrl(dataUrl: string): string {
        if (dataUrl?.startsWith("data:")) {
            const commaIndex = dataUrl.indexOf(",");
            if (commaIndex !== -1) {
                return dataUrl.substring(commaIndex + 1);
            }
        }
        return dataUrl;
    }

    /**
     * Handles delete tap on a thumbnail.
     */
    private handleThumbnailDelete(capturedImage: CapturedImage): void {
        this.removeCapturedImage(capturedImage.id);
    }

    /**
     * Notifies listeners that images have changed.
     */
    private notifyImagesChanged(): void {
        if (this._onImagesChanged) {
            this._onImagesChanged(this.images, this._bundleType);
        }
    }

    /**
     * Scrolls the thumbnail container to show the end (most recent image).
     */
    private scrollToEnd(): void {
        requestAnimationFrame(() => {
            const scroller = this._thumbnailScroller._element;
            scroller.scrollLeft = scroller.scrollWidth;
        });
    }

    /**
     * Gets the behavior instance for ImageBundle.
     * Provides device-specific sizing and behavior.
     */
    private get behavior(): ImageBundleBehavior {
        ImageBundle._behavior ??= BehaviorTypes.get("ImageBundle");
        return ImageBundle._behavior;
    }
}







